import { Component, Inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ConfirmDialogService, SaveForm, toMoment } from '@tokeet-frontend/tv3-platform'
import { Store } from '@ngrx/store'
import { UpdateAISettings } from '@tv3/containers/ai-responder/store/ai-settings.actions'
import * as moment from 'moment'
import {
  AI_OFF_CALENDAR_ITEM_DATE_FORMAT,
  AI_OFF_CALENDAR_ITEM_TIME_FORMAT,
  AiOffCalendarItem,
  AiOffCalendarItemView,
} from '@tv3/containers/ai-responder/store/ai-settings.model'
import {
  selectAIOffCalendarItemsForDate,
  selectAIOffCalendarItemsWithoutDate,
  selectAISettingsDisabledHours,
} from '@tv3/containers/ai-responder/store/ai-settings.selectors'

@Component({
  selector: 'app-schedule-ai-off-dialog',
  templateUrl: './schedule-ai-off-dialog.component.html',
  host: { class: 'modal-content' },
  styleUrls: ['./schedule-ai-off-dialog.component.scss'],
})
export class ScheduleAiOffDialogComponent {
  form = this.fb.group({
    timezone: [undefined, [Validators.required]],
  })
  fullDayOffCtrl = this.fb.control(false)
  applyToAllDaysCtrl = this.fb.control(false)

  readonly fullDayOffTooltip = $localize`Turn off AI for 24 hours`
  readonly applyToAllDaysTooltip = $localize`Turn off AI for all upcoming days or months to pause AI based guest messaging`

  timeSlots = Array.from({ length: 24 }, (_, i) => {
    const hour = i % 12 || 12
    const period = i < 12 ? 'AM' : 'PM'
    return { value: i, label: `${hour} ${period}`, checked: false }
  })

  allAIOffCalendarItems: AiOffCalendarItem[] = []
  otherAIOffCalendarItems: AiOffCalendarItemView[] = []
  allDaysAIOffCalendarItems: AiOffCalendarItemView[] = []

  constructor(
    public dialogRef: MatDialogRef<ScheduleAiOffDialogComponent>,
    private fb: FormBuilder,
    private store: Store<any>,
    private confirm: ConfirmDialogService,
    @Inject(MAT_DIALOG_DATA) public data: { date: number; timezone?: string }
  ) {}

  ngOnInit() {
    this.store.select(selectAIOffCalendarItemsForDate(this.data.date)).subscribe((items) => {
      this.timeSlots.forEach((slot) => {
        slot.checked = items.some((item) => this.isTimeSlotInRange(slot.value, item))
      })
      // Check if all time slots are selected (full day off)
      const allSlotsChecked = this.timeSlots.every((slot) => slot.checked)
      this.fullDayOffCtrl.setValue(allSlotsChecked, { emitEvent: false })
    })
    this.store.select(selectAIOffCalendarItemsWithoutDate(this.data.date)).subscribe((items) => {
      this.otherAIOffCalendarItems = items
      this.allDaysAIOffCalendarItems = items.filter((item) => item.all)
    })
    this.store.select(selectAISettingsDisabledHours).subscribe((items) => {
      this.allAIOffCalendarItems = items
    })

    this.setFormData()
    this.fullDayOffCtrl.valueChanges.subscribe((value) => {
      this.timeSlots.forEach((slot) => {
        slot.checked = value
      })
    })
  }

  isTimeSlotInRange(hour: number, item: AiOffCalendarItemView) {
    return toMoment(this.data.date).hour(hour).minute(0).second(0).isBetween(item.start, item.end, 'minutes', '[)')
  }

  getTimeSlotRanges() {
    const mergedTimeSlots: { from: number; to: number }[] = []
    let currentSlot: { from: number; to: number } | null = null
    for (const slot of this.timeSlots) {
      if (slot.checked) {
        if (!currentSlot) {
          currentSlot = { from: slot.value, to: slot.value }
        } else {
          if (currentSlot.to === slot.value - 1) {
            currentSlot.to = slot.value
          } else {
            mergedTimeSlots.push(currentSlot)
            currentSlot = { from: slot.value, to: slot.value }
          }
        }
      } else {
        if (currentSlot) {
          mergedTimeSlots.push(currentSlot)
          currentSlot = null
        }
      }
    }
    if (currentSlot) {
      mergedTimeSlots.push(currentSlot)
    }
    return mergedTimeSlots
  }

  removeAllDaysAIOffCalendarItem(item: AiOffCalendarItemView) {
    this.confirm
      .confirm({
        title: $localize`Remove this item?`,
        body: $localize`Are you sure you want to remove this item?`,
      })
      .subscribe(() => {
        this.store.dispatch(
          UpdateAISettings({
            payload: {
              disabledHours: this.allAIOffCalendarItems.filter((i) => i.from !== item.from && i.to !== item.to),
            },
          })
        )
      })
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { timezone } = form.getRawValue()
    const applyToAllDays = this.applyToAllDaysCtrl.value

    const mergedTimeSlots = this.getTimeSlotRanges()

    const disabledHours = mergedTimeSlots.map((slot) => {
      if (applyToAllDays) {
        const from = toMoment(this.data.date)
          .hour(slot.from)
          .minute(0)
          .second(0)
          .format(AI_OFF_CALENDAR_ITEM_TIME_FORMAT)
        const to = toMoment(this.data.date)
          .hour(slot.to + 1)
          .minute(0)
          .second(0)
          .format(AI_OFF_CALENDAR_ITEM_TIME_FORMAT)
        return { from, to }
      } else {
        const from = toMoment(this.data.date)
          .hour(slot.from)
          .minute(0)
          .second(0)
          .format(AI_OFF_CALENDAR_ITEM_DATE_FORMAT)
        const to = toMoment(this.data.date)
          .hour(slot.to + 1)
          .minute(0)
          .second(0)
          .format(AI_OFF_CALENDAR_ITEM_DATE_FORMAT)
        return { from, to }
      }
    })
    // Filter out existing items for the current date (they'll be replaced by new ones)
    const itemsToKeep = this.allAIOffCalendarItems.filter((item) => {
      // Keep items that are for all days
      if (item.from.length === 5) {
        // If we're saving for all days, remove existing all-day items that conflict
        if (applyToAllDays) {
          return !disabledHours.some((newItem) => newItem.from === item.from && newItem.to === item.to)
        }
        return true
      }
      // For date-specific items, only keep those that are NOT for the current date
      const itemDate = item.from.substring(0, 10) // Extract YYYY-MM-DD
      const currentDate = toMoment(this.data.date).format('YYYY-MM-DD')
      return itemDate !== currentDate
    })

    this.store.dispatch(
      UpdateAISettings({
        payload: {
          disabledHours: [...itemsToKeep, ...disabledHours],
          disabledTimezone: timezone,
        },
      })
    )
    this.close()
  }

  setFormData() {
    this.form.patchValue({
      timezone: this.data.timezone || moment.tz.guess(),
    })
  }

  close() {
    this.dialogRef.close()
  }
}
